import React, { useState, useRef } from 'react';
import { uploadProcedureImage, deleteProcedureImage, extractFilenameFromUrl } from '../services/procedureImageService';

const ConnectionProcedureEditor = ({ value, onChange }) => {
  const [activeView, setActiveView] = useState('edit'); // 'edit' ou 'preview'
  const [showImageUpload, setShowImageUpload] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(new Set()); // Track des uploads en cours
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);

  // Structure par défaut pour une procédure vide
  const defaultProcedure = {
    steps: [
      {
        id: Date.now(),
        title: 'Étape 1',
        content: '',
        type: 'text', // 'text', 'image', 'code', 'warning', 'info'
        imageUrl: null,
        imageName: null
      }
    ],
    notes: ''
  };

  // Parser le contenu existant (texte simple) vers la nouvelle structure
  const parseProcedure = (textContent) => {
    if (!textContent || textContent.trim() === '') {
      return defaultProcedure;
    }

    // Si c'est déjà du JSON structuré, le parser
    try {
      const parsed = JSON.parse(textContent);
      if (parsed.steps && Array.isArray(parsed.steps)) {
        return parsed;
      }
    } catch (e) {
      // Ce n'est pas du JSON, traiter comme du texte simple
    }

    // Convertir le texte simple en étapes
    const lines = textContent.split('\n').filter(line => line.trim() !== '');
    const steps = lines.map((line, index) => ({
      id: Date.now() + index,
      title: `Étape ${index + 1}`,
      content: line.trim(),
      type: 'text',
      imageUrl: null,
      imageName: null
    }));

    return {
      steps: steps.length > 0 ? steps : defaultProcedure.steps,
      notes: ''
    };
  };

  const [procedure, setProcedure] = useState(() => parseProcedure(value));

  // Sauvegarder la procédure au format JSON
  const saveProcedure = (newProcedure) => {
    setProcedure(newProcedure);
    const jsonString = JSON.stringify(newProcedure);
    onChange(jsonString);
  };

  // Ajouter une nouvelle étape
  const addStep = (type = 'text') => {
    const newStep = {
      id: Date.now(),
      title: `Étape ${procedure.steps.length + 1}`,
      content: '',
      type: type,
      imageUrl: null,
      imageName: null
    };

    const newProcedure = {
      ...procedure,
      steps: [...procedure.steps, newStep]
    };
    saveProcedure(newProcedure);
  };

  // Supprimer une étape
  const removeStep = async (stepId) => {
    if (procedure.steps.length <= 1) {
      alert('Une procédure doit contenir au moins une étape.');
      return;
    }

    const stepToRemove = procedure.steps.find(step => step.id === stepId);
    
    // Supprimer l'image du serveur si elle existe
    if (stepToRemove?.imageUrl) {
      try {
        const filename = extractFilenameFromUrl(stepToRemove.imageUrl);
        if (filename && !stepToRemove.imageUrl.startsWith('data:')) {
          await deleteProcedureImage(filename);
          console.log('🗑️ Image supprimée du serveur:', filename);
        }
      } catch (error) {
        console.warn('⚠️ Erreur suppression image:', error.message);
      }
    }

    const newProcedure = {
      ...procedure,
      steps: procedure.steps.filter(step => step.id !== stepId)
    };
    saveProcedure(newProcedure);
  };

  // Modifier une étape
  const updateStep = (stepId, field, value) => {
    const newProcedure = {
      ...procedure,
      steps: procedure.steps.map(step => 
        step.id === stepId ? { ...step, [field]: value } : step
      )
    };
    
    saveProcedure(newProcedure);
  };

  // Déplacer une étape
  const moveStep = (stepId, direction) => {
    const currentIndex = procedure.steps.findIndex(step => step.id === stepId);
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    
    if (newIndex < 0 || newIndex >= procedure.steps.length) return;
    
    const newSteps = [...procedure.steps];
    [newSteps[currentIndex], newSteps[newIndex]] = [newSteps[newIndex], newSteps[currentIndex]];
    
    const newProcedure = {
      ...procedure,
      steps: newSteps
    };
    saveProcedure(newProcedure);
  };

  // Gérer l'upload d'image vers le serveur
  const handleImageUpload = async (stepId, event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    setUploadingImages(prev => new Set([...prev, stepId]));
    
    try {
      // Upload vers le serveur
      const uploadResult = await uploadProcedureImage(file);
      
      // Mettre à jour l'étape avec l'URL du serveur
      const newProcedure = {
        ...procedure,
        steps: procedure.steps.map(step => 
          step.id === stepId ? { 
            ...step, 
            imageUrl: uploadResult.imageUrl,
            imageName: uploadResult.originalName
          } : step
        )
      };
      
      saveProcedure(newProcedure);
      
    } catch (error) {
      console.error('Erreur upload image:', error);
      alert(`Erreur lors de l'upload de l'image: ${error.message}`);
    } finally {
      setUploadingImages(prev => {
        const newSet = new Set(prev);
        newSet.delete(stepId);
        return newSet;
      });
      // Réinitialiser l'input file
      event.target.value = '';
    }
  };

  // Supprimer une image d'une étape
  const removeImageFromStep = async (stepId) => {
    const step = procedure.steps.find(s => s.id === stepId);
    if (!step?.imageUrl) return;
    
    try {
      // Supprimer du serveur si c'est une image uploadée
      if (!step.imageUrl.startsWith('data:')) {
        const filename = extractFilenameFromUrl(step.imageUrl);
        if (filename) {
          await deleteProcedureImage(filename);
          console.log('🗑️ Image supprimée du serveur');
        }
      }
      
      // Mettre à jour l'étape
      updateStep(stepId, 'imageUrl', null);
      updateStep(stepId, 'imageName', null);
      
    } catch (error) {
      console.warn('⚠️ Erreur suppression image:', error.message);
      // Supprimer quand même côté client
      updateStep(stepId, 'imageUrl', null);
      updateStep(stepId, 'imageName', null);
    }
  };

  // Rendu d'une étape en mode édition
  const renderStepEdit = (step, index) => {
    const isUploading = uploadingImages.has(step.id);
    
    return (
      <div key={step.id} className="border border-gray-200 rounded-lg p-4 mb-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            <input
              type="text"
              value={step.title}
              onChange={(e) => updateStep(step.id, 'title', e.target.value)}
              className="font-medium text-lg border-none p-0 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
              placeholder="Titre de l'étape"
            />
            <select
              value={step.type}
              onChange={(e) => updateStep(step.id, 'type', e.target.value)}
              className="text-sm border border-gray-300 rounded px-2 py-1"
            >
              <option value="text">📝 Texte</option>
              <option value="code">💻 Code/Commande</option>
              <option value="warning">⚠️ Attention</option>
              <option value="info">💡 Info</option>
              <option value="image">🖼️ Image</option>
            </select>
          </div>
          
          <div className="flex items-center space-x-1">
            {index > 0 && (
              <button
                type="button"
                onClick={() => moveStep(step.id, 'up')}
                className="p-1 text-gray-500 hover:text-blue-600"
                title="Monter"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                </svg>
              </button>
            )}
            {index < procedure.steps.length - 1 && (
              <button
                type="button"
                onClick={() => moveStep(step.id, 'down')}
                className="p-1 text-gray-500 hover:text-blue-600"
                title="Descendre"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            )}
            <button
              type="button"
              onClick={() => removeStep(step.id)}
              className="p-1 text-red-500 hover:text-red-700"
              title="Supprimer"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        </div>

        {step.type === 'image' ? (
          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleImageUpload(step.id, e)}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                disabled={isUploading}
              />
              {isUploading && (
                <div className="flex items-center text-blue-600">
                  <svg className="animate-spin h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Upload...
                </div>
              )}
            </div>
            <p className="text-xs text-gray-500">PNG, JPG, GIF jusqu'à 5MB - Images stockées sur le serveur</p>
            
            {step.imageUrl && !isUploading && (
              <div className="border border-gray-200 rounded p-2">
                <div className="flex justify-between items-start mb-2">
                  <div className="text-xs text-gray-600">
                    Image: {step.imageName} ({step.imageUrl.startsWith('data:') ? 'Base64' : 'Serveur'})
                  </div>
                  <button
                    type="button"
                    onClick={() => removeImageFromStep(step.id)}
                    className="text-red-500 hover:text-red-700 text-xs"
                    title="Supprimer l'image"
                  >
                    ❌
                  </button>
                </div>
                <img
                  src={step.imageUrl}
                  alt={step.imageName || 'Capture d\'écran'}
                  className="max-w-full h-auto max-h-64 object-contain"
                  onError={(e) => {
                    console.error('Erreur chargement image:', e);
                    console.log('URL de l\'image:', step.imageUrl?.substring(0, 100) + '...');
                  }}
                  onLoad={() => {
                    console.log('Image chargée avec succès');
                  }}
                />
              </div>
            )}
            <textarea
              value={step.content}
              onChange={(e) => updateStep(step.id, 'content', e.target.value)}
              placeholder="Description de l'image (optionnel)..."
              className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows="2"
            />
          </div>
        ) : (
          <textarea
            value={step.content}
            onChange={(e) => updateStep(step.id, 'content', e.target.value)}
            placeholder={getPlaceholderForType(step.type)}
            className={`w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${step.type === 'code' ? 'font-mono text-sm' : ''}`}
            rows={step.type === 'code' ? "4" : "3"}
          />
        )}
      </div>
    );
  };

  const getPlaceholderForType = (type) => {
    switch (type) {
      case 'code': return 'ssh user@server\ncd /path/to/app\nsudo systemctl restart service';
      case 'warning': return 'Attention : Vérifiez que le service soit arrêté avant de continuer...';
      case 'info': return 'Info : Cette étape peut prendre plusieurs minutes...';
      default: return 'Décrivez cette étape de la procédure...';
    }
  };

  // Rendu d'une étape en mode aperçu
  const renderStepPreview = (step, index) => {
    const getStepStyle = (type) => {
      switch (type) {
        case 'warning': return 'border-l-4 border-amber-500 bg-amber-50 p-4';
        case 'info': return 'border-l-4 border-blue-500 bg-blue-50 p-4';
        case 'code': return 'bg-gray-900 text-gray-100 p-4 rounded font-mono text-sm';
        default: return 'p-4';
      }
    };

    return (
      <div key={step.id} className="mb-6">
        <h3 className="text-lg font-semibold mb-3 flex items-center">
          <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm mr-3">
            {index + 1}
          </span>
          {step.title}
        </h3>
        
        <div className={getStepStyle(step.type)}>
          {step.type === 'image' && step.imageUrl && (
            <div className="mb-4">
              <img
                src={step.imageUrl}
                alt={step.imageName || 'Capture d\'écran'}
                className="max-w-full h-auto border border-gray-200 rounded shadow-sm"
              />
              {step.imageName && (
                <p className="text-sm text-gray-500 mt-2 italic">{step.imageName}</p>
              )}
            </div>
          )}
          
          {step.content && (
            <div className={step.type === 'code' ? '' : 'whitespace-pre-wrap'}>
              {step.content}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <label className="block text-gray-700 text-lg font-medium">
          📋 Procédure de connexion
        </label>
        
        <div className="flex items-center space-x-2">
          <div className="flex border border-gray-300 rounded-md">
            <button
              type="button"
              onClick={() => setActiveView('edit')}
              className={`px-3 py-1 text-sm font-medium rounded-l-md ${
                activeView === 'edit' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
              }`}
            >
              ✏️ Éditer
            </button>
            <button
              type="button"
              onClick={() => setActiveView('preview')}
              className={`px-3 py-1 text-sm font-medium rounded-r-md ${
                activeView === 'preview' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
              }`}
            >
              👁️ Aperçu
            </button>
          </div>
        </div>
      </div>

      {activeView === 'edit' ? (
        <div>
          {/* Mode édition */}
          <div className="mb-4">
            {procedure.steps.map((step, index) => renderStepEdit(step, index))}
          </div>
          
          {/* Boutons d'ajout */}
          <div className="flex flex-wrap gap-2 mb-4">
            <button
              type="button"
              onClick={() => addStep('text')}
              className="px-3 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 flex items-center"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              📝 Étape Texte
            </button>
            <button
              type="button"
              onClick={() => addStep('image')}
              className="px-3 py-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 flex items-center"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              🖼️ Étape Image
            </button>
            <button
              type="button"
              onClick={() => addStep('code')}
              className="px-3 py-2 bg-gray-800 text-white text-sm rounded-md hover:bg-gray-900 flex items-center"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              💻 Code/Commande
            </button>
            <button
              type="button"
              onClick={() => addStep('warning')}
              className="px-3 py-2 bg-amber-600 text-white text-sm rounded-md hover:bg-amber-700 flex items-center"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              ⚠️ Avertissement
            </button>
          </div>
          
          {/* Notes générales */}
          <div className="mt-6">
            <label className="block text-gray-700 mb-2 font-medium">📝 Notes générales (optionnel)</label>
            <textarea
              value={procedure.notes}
              onChange={(e) => saveProcedure({ ...procedure, notes: e.target.value })}
              placeholder="Informations complémentaires, contacts, remarques..."
              className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows="3"
            />
          </div>
        </div>
      ) : (
        <div>
          {/* Mode aperçu */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h2 className="text-xl font-bold mb-6 text-gray-800">Procédure de connexion</h2>
            
            {procedure.steps.length > 0 ? (
              <div className="space-y-6">
                {procedure.steps.map((step, index) => renderStepPreview(step, index))}
              </div>
            ) : (
              <p className="text-gray-500 italic">Aucune étape définie</p>
            )}
            
            {procedure.notes && (
              <div className="mt-8 pt-6 border-t border-gray-200">
                <h3 className="font-semibold mb-3 text-gray-800">📝 Notes générales</h3>
                <div className="bg-gray-50 p-4 rounded-md">
                  <p className="whitespace-pre-wrap text-gray-700">{procedure.notes}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ConnectionProcedureEditor;
